import axios, { AxiosError } from 'axios';
import { getToken } from '../utils/common';
import {
  CVProfile,
  EducationEntry,
  ExperienceEntry,
  ProjectEntry,
  AchievementEntry,
  LanguageEntry,
  PortfolioEntry,
  TechnicalInterestUpdate,
  SoftSkillUpdate,
  HardSkillUpdate,
  PDFExportResult,
  CVAnalysisResult,
  CVService as ICVService
} from '../components/CVBuilder/types';

/**
 * Custom error class for CV service operations
 */
export class CVServiceError extends Error {
  public readonly statusCode?: number;
  public readonly isRetryable: boolean;
  public readonly originalError?: Error;

  constructor(
    message: string, 
    statusCode?: number, 
    originalError?: Error,
    isRetryable: boolean = false
  ) {
    super(message);
    this.name = 'CVServiceError';
    this.statusCode = statusCode;
    this.originalError = originalError;
    this.isRetryable = isRetryable;
  }
}

/**
 * CV Service for managing CV data operations
 * Implements the CVService interface defined in the CV Builder types
 */
class CVService implements ICVService {
  private readonly baseUrl = "/cv";

  /**
   * Make authenticated HTTP request with enhanced error handling and retry logic
   */
  private async makeRequest(
    method: string,
    url: string,
    data?: any,
    config?: any
  ): Promise<any> {
    const isFormData = data instanceof FormData;

    const defaultHeaders = isFormData
      ? {
          Accept: "application/json",
          Authorization: `Bearer ${getToken()}`,
        }
      : {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        };

    const apiUrl = process.env.REACT_APP_API_URL + url;
    const requestConfig = {
      method,
      url: apiUrl,
      headers: { ...defaultHeaders, ...config?.headers },
      timeout: 30000, // 30 second timeout
      ...config,
    };

    if (data && ["POST", "PUT", "PATCH"].includes(method.toUpperCase())) {
      requestConfig.data = data;
    } else if (data && method.toUpperCase() === "GET") {
      requestConfig.params = data;
    }

    try {
      const response = await axios(requestConfig);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Enhanced error handling with proper error classification
   */
  private handleError(error: any): CVServiceError {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      // Network or timeout errors - retryable
      if (!axiosError.response) {
        return new CVServiceError(
          "Network error. Please check your connection and try again.",
          undefined,
          axiosError,
          true
        );
      }

      const status = axiosError.response.status;
      const responseData = axiosError.response.data as any;

      // Extract error message from response
      let message = "An error occurred. Please try again.";
      if (responseData?.message) {
        message = responseData.message;
      } else if (responseData?.error) {
        message = responseData.error;
      } else if (typeof responseData === "string") {
        message = responseData;
      }

      // Determine if error is retryable based on status code
      const isRetryable = status >= 500 || status === 408 || status === 429;

      // Specific error messages for common status codes
      switch (status) {
        case 400:
          message =
            responseData?.message ||
            "Invalid request. Please check your input.";
          break;
        case 401:
          message = "Authentication failed. Please log in again.";
          break;
        case 403:
          message = "You do not have permission to perform this action.";
          break;
        case 404:
          message = "The requested resource was not found.";
          break;
        case 408:
          message = "Request timeout. Please try again.";
          break;
        case 409:
          message = responseData?.message || "Conflict with existing data.";
          break;
        case 422:
          message =
            responseData?.message ||
            "Validation failed. Please check your input.";
          break;
        case 429:
          message = "Too many requests. Please wait a moment and try again.";
          break;
        case 500:
          message = "Server error. Please try again later.";
          break;
        case 502:
        case 503:
        case 504:
          message = "Service temporarily unavailable. Please try again later.";
          break;
      }

      return new CVServiceError(message, status, axiosError, isRetryable);
    }

    // Non-Axios errors
    if (error instanceof Error) {
      return new CVServiceError(
        error.message || "An unexpected error occurred.",
        undefined,
        error,
        false
      );
    }

    // Unknown error type
    return new CVServiceError(
      "An unknown error occurred. Please try again.",
      undefined,
      undefined,
      false
    );
  }

  /**
   * Get CV profile for the current user
   */
  async getCVByUser(): Promise<CVProfile | null> {
    try {
      const response = await this.makeRequest("GET", `${this.baseUrl}/me`);
      return response?.data?.cv || null;
    } catch (error: any) {
      if (
        error.message?.includes("404") ||
        error.message?.includes("not found")
      ) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create a new CV profile
   */
  async createCV(cvData: any): Promise<CVProfile> {
    // Check if we have a photo file to upload
    if (cvData.photo && cvData.photo instanceof File) {
      const formData = new FormData();

      // Add all CV data to FormData
      Object.entries(cvData).forEach(([key, value]) => {
        if (key !== "photo" && value !== undefined && value !== null) {
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      // Add photo file
      formData.append("photo", cvData.photo);

      const response = await this.makeRequest("POST", this.baseUrl, formData, {
        headers: {
          // Don't set Content-Type, let browser set it with boundary for FormData
          Accept: "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });
      return response?.data?.cv || response;
    } else {
      // No photo, use regular JSON request
      const { photo, ...cleanData } = cvData;
      const response = await this.makeRequest("POST", this.baseUrl, cleanData);
      return response?.data?.cv || response;
    }
  }

  /**
   * Update an existing CV profile
   */
  async updateCV(id: number, cvData: any): Promise<CVProfile> {
    // Check if we have a photo file to upload
    if (cvData.photo && cvData.photo instanceof File) {
      const formData = new FormData();

      // Add all CV data to FormData
      Object.entries(cvData).forEach(([key, value]) => {
        if (key !== "photo" && value !== undefined && value !== null) {
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      // Add photo file
      formData.append("photo", cvData.photo);

      const response = await this.makeRequest(
        "PUT",
        `${this.baseUrl}/${id}`,
        formData,
        {
          headers: {
            // Don't set Content-Type, let browser set it with boundary for FormData
            Accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      return response?.data?.cv || response;
    } else {
      // No photo, use regular JSON request
      const { photo, ...cleanData } = cvData;
      const response = await this.makeRequest(
        "PUT",
        `${this.baseUrl}/${id}`,
        cleanData
      );
      return response?.data?.cv || response;
    }
  }

  /**
   * Delete a CV profile
   */
  async deleteCV(id: number): Promise<void> {
    await this.makeRequest("DELETE", `${this.baseUrl}/${id}`);
  }

  // Entry management methods

  /**
   * Add education entry to CV
   */
  async addEducation(
    cvId: number,
    education: EducationEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "POST",
      `${this.baseUrl}/${cvId}/education`,
      education
    );
    return response?.data?.cv || response;
  }

  /**
   * Update education entry in CV
   */
  async updateEducation(
    cvId: number,
    educationId: number,
    education: EducationEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/education/${educationId}`,
      education
    );
    return response?.data?.cv || response;
  }

  /**
   * Delete education entry from CV
   */
  async deleteEducation(cvId: number, educationId: number): Promise<CVProfile> {
    const response = await this.makeRequest(
      "DELETE",
      `${this.baseUrl}/${cvId}/education/${educationId}`
    );
    return response?.data?.cv || response;
  }

  /**
   * Add experience entry to CV
   */
  async addExperience(
    cvId: number,
    experience: ExperienceEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "POST",
      `${this.baseUrl}/${cvId}/experience`,
      experience
    );
    return response?.data?.cv || response;
  }

  /**
   * Update experience entry in CV
   */
  async updateExperience(
    cvId: number,
    experienceId: number,
    experience: ExperienceEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/experience/${experienceId}`,
      experience
    );
    return response?.data?.cv || response;
  }

  /**
   * Delete experience entry from CV
   */
  async deleteExperience(
    cvId: number,
    experienceId: number
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "DELETE",
      `${this.baseUrl}/${cvId}/experience/${experienceId}`
    );
    return response?.data?.cv || response;
  }

  /**
   * Add project entry to CV
   */
  async addProject(cvId: number, project: ProjectEntry): Promise<CVProfile> {
    const response = await this.makeRequest(
      "POST",
      `${this.baseUrl}/${cvId}/project`,
      project
    );
    return response?.data?.cv || response;
  }

  /**
   * Update project entry in CV
   */
  async updateProject(
    cvId: number,
    projectId: number,
    project: ProjectEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/project/${projectId}`,
      project
    );
    return response?.data?.cv || response;
  }

  /**
   * Delete project entry from CV
   */
  async deleteProject(cvId: number, projectId: number): Promise<CVProfile> {
    const response = await this.makeRequest(
      "DELETE",
      `${this.baseUrl}/${cvId}/project/${projectId}`
    );
    return response?.data?.cv || response;
  }

  /**
   * Add achievement entry to CV
   */
  async addAchievement(
    cvId: number,
    achievement: AchievementEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "POST",
      `${this.baseUrl}/${cvId}/achievement`,
      achievement
    );
    return response?.data?.cv || response;
  }

  /**
   * Update achievement entry in CV
   */
  async updateAchievement(
    cvId: number,
    achievementId: number,
    achievement: AchievementEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/achievement/${achievementId}`,
      achievement
    );
    return response?.data?.cv || response;
  }

  /**
   * Delete achievement entry from CV
   */
  async deleteAchievement(
    cvId: number,
    achievementId: number
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "DELETE",
      `${this.baseUrl}/${cvId}/achievement/${achievementId}`
    );
    return response?.data?.cv || response;
  }

  /**
   * Add language entry to CV
   */
  async addLanguage(cvId: number, language: LanguageEntry): Promise<CVProfile> {
    const response = await this.makeRequest(
      "POST",
      `${this.baseUrl}/${cvId}/language`,
      language
    );
    return response?.data?.cv || response;
  }

  /**
   * Update language entry in CV
   */
  async updateLanguage(
    cvId: number,
    languageId: number,
    language: LanguageEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/language/${languageId}`,
      language
    );
    return response?.data?.cv || response;
  }

  /**
   * Delete language entry from CV
   */
  async deleteLanguage(cvId: number, languageId: number): Promise<CVProfile> {
    const response = await this.makeRequest(
      "DELETE",
      `${this.baseUrl}/${cvId}/language/${languageId}`
    );
    return response?.data?.cv || response;
  }

  /**
   * Add portfolio entry to CV
   */
  async addPortfolio(
    cvId: number,
    portfolio: PortfolioEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "POST",
      `${this.baseUrl}/${cvId}/portfolio`,
      portfolio
    );
    return response?.data?.cv || response;
  }

  /**
   * Update portfolio entry in CV
   */
  async updatePortfolio(
    cvId: number,
    portfolioId: number,
    portfolio: PortfolioEntry
  ): Promise<CVProfile> {
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/portfolio/${portfolioId}`,
      portfolio
    );
    return response?.data?.cv || response;
  }

  /**
   * Delete portfolio entry from CV
   */
  async deletePortfolio(cvId: number, portfolioId: number): Promise<CVProfile> {
    const response = await this.makeRequest(
      "DELETE",
      `${this.baseUrl}/${cvId}/portfolio/${portfolioId}`
    );
    return response?.data?.cv || response;
  }

  // Skills management methods

  /**
   * Update technical interests for CV
   */
  async updateTechnicalInterests(
    cvId: number,
    interests: TechnicalInterestUpdate
  ): Promise<CVProfile> {
    const requestData = {
      selected_interests: interests.interests,
      custom_interests: interests.customInterests,
    };
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/technical-interests`,
      requestData
    );
    return response?.data?.cv || response;
  }

  /**
   * Update soft skills for CV
   */
  async updateSoftSkills(
    cvId: number,
    skills: SoftSkillUpdate
  ): Promise<CVProfile> {
    const requestData = {
      selected_skills: skills.skills,
    };
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/soft-skills`,
      requestData
    );
    return response?.data?.cv || response;
  }

  /**
   * Update hard skills for CV
   */
  async updateHardSkills(
    cvId: number,
    skills: HardSkillUpdate
  ): Promise<CVProfile> {
    const requestData = {
      skills: skills.skills,
    };
    const response = await this.makeRequest(
      "PUT",
      `${this.baseUrl}/${cvId}/hard-skills`,
      requestData
    );
    return response?.data?.cv || response;
  }

  // Options methods

  /**
   * Get available technical interest options
   */
  async getTechnicalInterestOptions(): Promise<string[]> {
    const response = await this.makeRequest(
      "GET",
      "/cv/options/technical-interests"
    );
    return response?.data?.options || response?.options || [];
  }

  /**
   * Get available soft skill options
   */
  async getSoftSkillOptions(): Promise<string[]> {
    const response = await this.makeRequest("GET", "/cv/options/soft-skills");
    return response?.data?.options || response?.options || [];
  }

  // Export methods

  /**
   * Export CV to PDF
   */
  async exportToPDF(cvId: number): Promise<PDFExportResult> {
    try {
      const response = await this.makeRequest(
        "POST",
        `${this.baseUrl}/${cvId}/export-pdf`
      );
      const data = response?.data || response;
      return {
        success: true,
        downloadUrl: data.download_url,
        filename: data.filename,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "PDF export failed",
      };
    }
  }

  async analyzeCV(cvId: number): Promise<CVAnalysisResult> {
    try {
      const response = await this.makeRequest(
        "POST",
        `${this.baseUrl}/${cvId}/ats-analysis`
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Analyze failed",
      };
    }
  }

  /**
   * Download PDF file
   */
  async downloadPDF(cvId: number, filename: string): Promise<Blob> {
    const response = await this.makeRequest(
      "GET",
      `${this.baseUrl}/${cvId}/download-pdf`,
      { filename },
      {
        responseType: "blob",
      }
    );
    return response;
  }
}

// Export singleton instance
export const cvService = new CVService();
export default cvService;